import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ShoppingCart,
  LayoutGrid,
  List,
  ArrowUp,
  ArrowDown,
  Loader2
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  TableRowActions,
  TableRowActionOption
} from '@/components/ui/table-row-actions';
import { getSortOptions } from '../utils/propertyListUtils';
import { LayoutTypes, TableActionBarProps } from './model/tableActionBar';
import { setCartCount } from '../slice/OrderCartSlice';
import { useAddPropertiesToCart } from '../hooks/useAddPropertiesToCart';
import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { canCreateOnRoute } from '../../../../util/permissions';
import type { ListingSort } from '../listingPaging';

/** The menu option a restored sort selects, or the menu's default when the
 *  sort isn't one it offers (a column-header sort, say). */
const initialSortOption = (
  initialSort: ListingSort | null,
  isProperty: boolean,
): TableRowActionOption => {
  const match = initialSort
    ? getSortOptions(null, isProperty).find((option) => option.key === initialSort.selector)
    : undefined;
  if (match && initialSort) return { ...match, active: true, asc: initialSort.asc };
  return {
    id: '',
    text: isProperty ? 'Last Modified' : 'Order Date',
    key: isProperty ? 'lastModified' : 'orderDate',
    active: true,
    asc: true
  };
};

export const TableActionBar = ({
  onActionClick,
  isProperty,
  isCardView,
  onTableSort,
  initialSort = null,
  selection,
}: TableActionBarProps) => {
  // Highlight derives from the parent's isCardView so the buttons stay in
  // sync after a toggle. TableActionBar is mounted inside the card-view /
  // list-view conditional branches in PropertyListPage, so flipping the
  // view unmounts one instance and mounts the other; reading from
  // localStorage here would re-init from stale data because the parent's
  // persistence effect runs after commit.
  const radioValue = isCardView ? LayoutTypes.CARD : LayoutTypes.LIST;

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeSortOption, setActiveSortOption] = useState<TableRowActionOption>(
    () => initialSortOption(initialSort, isProperty),
  );

  const hasSelection = selection.selectedIds.size > 0;
  const dispatch = useDispatch();
  const addPropertiesToCart = useAddPropertiesToCart();

  const onRadioChange = (value: string) => {
    onActionClick(value);
  };

  const onSort = () => {
    if (activeSortOption) {
      activeSortOption.asc = !(activeSortOption.asc || false);
    }
    onTableSort(activeSortOption?.key, activeSortOption?.asc);
  };

  const onSelectOption = (option: TableRowActionOption) => {
    setActiveSortOption(option);
    onTableSort(option.key, option.asc);
  };

  const getSortTitle = (): string => {
    if (activeSortOption?.asc === false) return 'Descending';
    if (activeSortOption?.asc === true) return 'Ascending';
    return '';
  };

  const openCartOrder = async () => {
    if (isAddingToCart) return;
    setIsAddingToCart(true);

    try {
      const selectedIds = Array.from(selection.selectedIds);
      const { itemsCount, addedCount } = await addPropertiesToCart(selectedIds);
      dispatch(setCartCount(itemsCount));

      if (addedCount > 0) {
        const text = addedCount === 1 ? 'property' : 'properties';
        dispatch(
          showAlert({
            type: Variants.SUCCESS,
            message: `${addedCount} ${text} successfully added to cart`,
          })
        );
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  const SortIcon = activeSortOption?.asc ? ArrowUp : ArrowDown;

  return (
    <section className="flex items-center">
      {/* Left: action buttons. Bulk add-to-cart hides for roles the cart
          route rejects (DV-2751) — same gate as the per-row AddToCartButton. */}
      <div className="flex gap-2">
        {isProperty && canCreateOnRoute('Order') && (
          <Button
            variant="outline"
            disabled={!hasSelection || isAddingToCart}
            onClick={openCartOrder}
            className="gap-1.5"
          >
            {isAddingToCart ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShoppingCart className="h-4 w-4" />
            )}
            Add to Cart
          </Button>
        )}
      </div>

      {/* Right: sort controls */}
      <div className="ml-auto flex items-center gap-2">
        {/*
          Discrete sort widget hidden on the orders list 2026-05-07 — column headers
          already drive sorting, and the standalone control was redundant. Property
          view keeps it pending its own per-page walk; restore for orders if product
          wants the dedicated affordance back.
        */}
        {isProperty && (
          <TableRowActions
            title={getSortTitle()}
            data={{ id: 'property-order-sorting' }}
            headerIcon={
              <Button variant="link" onClick={onSort} aria-label="Toggle Sort Direction">
                <SortIcon className="h-5 w-5" />
              </Button>
            }
            options={getSortOptions(activeSortOption, isProperty)}
            onMoreOptionClick={onSelectOption}
          >
            <Button
              variant="outline"
              size="icon"
              title="Sorting"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </TableRowActions>
        )}

        {/* Card/List toggle (property view only) */}
        {isProperty && (
          <div className="hidden lg:inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              className={`flex items-center justify-center px-3 py-2.5 border-none cursor-pointer transition-colors ${
                radioValue === LayoutTypes.LIST
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-foreground hover:bg-muted'
              }`}
              onClick={() => onRadioChange(LayoutTypes.LIST)}
              aria-label="List View"
            >
              <List className="h-5 w-5" />
            </button>
            <button
              type="button"
              className={`flex items-center justify-center px-3 py-2.5 border-none border-l border-border cursor-pointer transition-colors ${
                radioValue === LayoutTypes.CARD
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-foreground hover:bg-muted'
              }`}
              onClick={() => onRadioChange(LayoutTypes.CARD)}
              aria-label="Card View"
            >
              <LayoutGrid className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
